export function parseMarkdown(text: string): JSX.Element {
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let currentList: JSX.Element[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let inCodeBlock = false;
  let codeBlockLines: string[] = [];
  let codeBlockLang = '';
  let tableRows: string[] = [];
  let blockquoteLines: { index: number; content: string }[] = [];

  const processInlineFormatting = (line: string): (string | JSX.Element)[] => {
    const parts: (string | JSX.Element)[] = [];
    let remaining = line;
    let key = 0;

    while (remaining.length > 0) {
      const nextBold = remaining.indexOf('**');
      const nextCode = remaining.indexOf('`');
      const nextItalic = remaining.search(/(?<!\*)\*(?!\*)/);

      const candidates: { type: string; pos: number }[] = [];
      if (nextBold >= 0) candidates.push({ type: 'bold', pos: nextBold });
      if (nextCode >= 0) candidates.push({ type: 'code', pos: nextCode });
      if (nextItalic >= 0) candidates.push({ type: 'italic', pos: nextItalic });

      if (candidates.length === 0) {
        parts.push(remaining);
        break;
      }

      candidates.sort((a, b) => a.pos - b.pos);
      const first = candidates[0];

      if (first.pos > 0) {
        parts.push(remaining.slice(0, first.pos));
        remaining = remaining.slice(first.pos);
        continue;
      }

      if (first.type === 'bold') {
        const endBold = remaining.indexOf('**', 2);
        if (endBold > 2) {
          parts.push(
            <strong key={`b-${key++}`} className="font-semibold">
              {remaining.slice(2, endBold)}
            </strong>
          );
          remaining = remaining.slice(endBold + 2);
          continue;
        }
      }

      if (first.type === 'code') {
        const endCode = remaining.indexOf('`', 1);
        if (endCode > 1) {
          parts.push(
            <code key={`c-${key++}`} className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-[0.85em] font-mono">
              {remaining.slice(1, endCode)}
            </code>
          );
          remaining = remaining.slice(endCode + 1);
          continue;
        }
      }

      if (first.type === 'italic') {
        const afterStar = remaining.slice(1);
        const endItalic = afterStar.search(/(?<!\*)\*(?!\*)/);
        if (endItalic > 0) {
          parts.push(
            <em key={`i-${key++}`}>{afterStar.slice(0, endItalic)}</em>
          );
          remaining = remaining.slice(endItalic + 2);
          continue;
        }
      }

      parts.push(remaining[0]);
      remaining = remaining.slice(1);
    }

    return parts;
  };

  const flushList = () => {
    if (currentList.length > 0 && listType) {
      const ListTag = listType;
      const listClass = listType === 'ol'
        ? 'mt-4 mb-5 ml-6 list-decimal space-y-2.5'
        : 'mt-4 mb-5 ml-6 list-disc space-y-2.5';
      elements.push(
        <ListTag key={`list-${elements.length}`} className={listClass}>
          {currentList}
        </ListTag>
      );
      currentList = [];
      listType = null;
    }
  };

  const flushBlockquote = () => {
    if (blockquoteLines.length > 0) {
      const key = `bq-${blockquoteLines[0].index}`;
      elements.push(
        <blockquote key={key} className="my-3 border-l-[3px] border-gray-300 pl-4">
          {blockquoteLines.map((item) => (
            <p key={`bqp-${item.index}`} className="my-1.5 leading-relaxed text-gray-700">
              {processInlineFormatting(item.content)}
            </p>
          ))}
        </blockquote>
      );
      blockquoteLines = [];
    }
  };

  const flushTable = () => {
    if (tableRows.length < 2) {
      tableRows.forEach((row, i) => {
        elements.push(
          <p key={`tp-${elements.length}-${i}`} className="my-2">
            {processInlineFormatting(row)}
          </p>
        );
      });
      tableRows = [];
      return;
    }

    const parseRow = (row: string) => {
      return row
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());
    };

    const headerCells = parseRow(tableRows[0]);
    const isSeparator = (row: string) => /^\|?[\s\-:|]+\|?$/.test(row);

    let dataStartIndex = 1;
    if (tableRows.length > 1 && isSeparator(tableRows[1])) {
      dataStartIndex = 2;
    }

    const dataRows = tableRows.slice(dataStartIndex).map(parseRow);

    elements.push(
      <div key={`table-${elements.length}`} className="my-3 overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {headerCells.map((cell, i) => (
                <th key={`th-${i}`} className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-widest font-sans">
                  {processInlineFormatting(cell)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, ri) => (
              <tr key={`tr-${ri}`} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                {row.map((cell, ci) => (
                  <td key={`td-${ri}-${ci}`} className="px-4 py-2 text-gray-600 border-t border-gray-100">
                    {processInlineFormatting(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    tableRows = [];
  };

  const isTableRow = (line: string) => {
    const trimmed = line.trim();
    return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.includes('|');
  };

  const isTableSeparator = (line: string) => /^\|?[\s\-:|]+\|?$/.test(line.trim());

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (trimmed.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${index}`} className="my-3 bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm font-mono">
            <code>{codeBlockLines.join('\n')}</code>
          </pre>
        );
        codeBlockLines = [];
        codeBlockLang = '';
        inCodeBlock = false;
      } else {
        flushList();
        flushTable();
        inCodeBlock = true;
        codeBlockLang = trimmed.slice(3).trim();
      }
      return;
    }

    if (inCodeBlock) {
      codeBlockLines.push(line);
      return;
    }

    if (isTableRow(trimmed) || (tableRows.length > 0 && isTableSeparator(trimmed))) {
      flushList();
      tableRows.push(trimmed);
      return;
    }

    if (tableRows.length > 0) {
      flushTable();
    }

    if (!trimmed) {
      flushList();
      flushBlockquote();
      return;
    }

    if (/^[-*_]{3,}$/.test(trimmed)) {
      flushList();
      elements.push(
        <hr key={`hr-${index}`} className="my-4 border-t border-gray-200" />
      );
      return;
    }

    const headerMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      flushList();
      const level = headerMatch[1].length;
      const text = headerMatch[2];
      const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements;
      const className = level === 1 ? 'text-xl font-bold mt-8 mb-4 text-gray-900' :
                       level === 2 ? 'text-lg font-bold mt-7 mb-3 text-gray-900' :
                       level === 3 ? 'text-base font-semibold mt-6 mb-2.5 text-gray-800' :
                       'text-sm font-semibold mt-5 mb-2.5 text-gray-800';
      elements.push(
        <HeadingTag key={`h${level}-${index}`} className={className}>
          {processInlineFormatting(text)}
        </HeadingTag>
      );
      return;
    }

    const ulMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (ulMatch) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      currentList.push(
        <li key={`li-${index}`} className="text-gray-700 leading-relaxed pl-1">
          {processInlineFormatting(ulMatch[1])}
        </li>
      );
      return;
    }

    const olMatch = trimmed.match(/^\d+\.\s+(.+)$/);
    if (olMatch) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      currentList.push(
        <li key={`li-${index}`} className="text-gray-700 leading-relaxed pl-1">
          {processInlineFormatting(olMatch[1])}
        </li>
      );
      return;
    }

    if (trimmed.startsWith('> ') || trimmed === '>') {
      flushList();
      const content = trimmed === '>' ? '' : trimmed.slice(2);
      if (content) {
        blockquoteLines.push({ index, content });
      }
      return;
    }

    if (blockquoteLines.length > 0) {
      flushBlockquote();
    }

    flushList();
    elements.push(
      <p key={`p-${index}`} className="my-3.5 leading-relaxed text-gray-700">
        {processInlineFormatting(trimmed)}
      </p>
    );
  });

  if (inCodeBlock && codeBlockLines.length > 0) {
    elements.push(
      <pre key={`code-end`} className="my-3 bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm font-mono">
        <code>{codeBlockLines.join('\n')}</code>
      </pre>
    );
  }

  flushList();
  flushBlockquote();
  flushTable();

  return <div className="space-y-3">{elements}</div>;
}
